import React, { useCallback, useEffect, useState } from 'react';
import Sidebar from '../../components/Sidebar';
import Header from '../../components/header';
import Modal from '../../components/modal';
import { useUnits } from '../../context/unitsContext';
import { useUsers } from '../../context/usersContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencilAlt, faSearch } from '@fortawesome/free-solid-svg-icons';
import PlusCircle from '../../assets/icons/pluscircle.svg';
import ReactPaginate from 'react-paginate';
import '../Unidades/unidades.css';
import './educacao.css';
import useSubmitLock from '../../utils/useSubmitLock';

const parseUnitId = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const resolveItemUnitId = (item) => parseUnitId(
  item?.unit?.id ?? item?.unit_id ?? item?.unit
);


function AcervoEducacao() {
  const { listCollection, createCollection, listBooks, createBook, editBook } = useUnits();
  const { effectiveUnitId, userUnits } = useUsers();
  const unitAcronym = Array.isArray(userUnits)
    ? userUnits.find((unit) => String(unit?.id) === String(effectiveUnitId))?.acronym
    : undefined;
  const { isSubmitting, runWithSubmitLock } = useSubmitLock();

  const [collections, setCollections] = useState([]);
  const [selectedCollectionId, setSelectedCollectionId] = useState(null);
  const [isCollectionModalOpen, setIsCollectionModalOpen] = useState(false);
  const [newCollectionData, setNewCollectionData] = useState({
    control: true,
    maintenance: 'Demanda',
    unit: null,
  });

  const [books, setBooks] = useState([]);
  const [totalBooks, setTotalBooks] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 10;
  const [titleQuery, setTitleQuery] = useState('');
  const [debouncedTitleQuery, setDebouncedTitleQuery] = useState('');
  const [isBookModalOpen, setIsBookModalOpen] = useState(false);
  const [isEditBookModalOpen, setIsEditBookModalOpen] = useState(false);
  const [editBookData, setEditBookData] = useState(null);
  const [newBookData, setNewBookData] = useState({
    entry_date: '',
    title: '',
    author: '',
    edition: '',
    publisher: '',
    publication_date: '',
    number_pages: '',
    gender: '',
    isbn: '',
    isbn_two: '',
    translators: '',
    original: true,
    status: '',
    origem: '',
    collection: '',
  });

  useEffect(() => {
    setNewCollectionData((prev) => ({
      ...prev,
      unit: effectiveUnitId ?? null,
    }));
  }, [effectiveUnitId]);

  const resetNewBookData = () => {
    setNewBookData({
      entry_date: '',
      title: '',
      author: '',
      edition: '',
      publisher: '',
      publication_date: '',
      number_pages: '',
      gender: '',
      isbn: '',
      isbn_two: '',
      translators: '',
      original: true,
      status: '',
      origem: '',
      collection: '',
    });
  };

  const fetchCollections = useCallback(async () => {
    if (effectiveUnitId === null || effectiveUnitId === undefined) {
      setCollections([]);
      setSelectedCollectionId(null);
      return;
    }

    const response = await listCollection(effectiveUnitId);
    const responseResults = Array.isArray(response?.results) ? response.results : [];
    const unitCollections = responseResults.filter((collection) => {
      const itemUnitId = resolveItemUnitId(collection);
      if (itemUnitId === null) return true;
      return String(itemUnitId) === String(effectiveUnitId);
    });

    setCollections(unitCollections);
    setSelectedCollectionId((prev) => {
      if (unitCollections.length === 0) return null;
      if (prev && unitCollections.some((item) => String(item.id) === String(prev))) return prev;
      return unitCollections[0].id;
    });
  }, [effectiveUnitId, listCollection]);

  useEffect(() => {
    fetchCollections();
  }, [fetchCollections]);

  // Busca por título só é disparada a partir de 3 letras digitadas
  useEffect(() => {
    const query = titleQuery.trim();

    if (query.length > 0 && query.length < 3) {
      return;
    }

    const timer = setTimeout(() => {
      setDebouncedTitleQuery(query);
      setCurrentPage(0);
    }, 350);

    return () => clearTimeout(timer);
  }, [titleQuery]);

  // O filtro "collection" da API é ignorado pelo backend, então busca-se todos os
  // livros da unidade (via unit_acronym, o único filtro que funciona, mais "title"
  // quando informado) e filtra-se e pagina-se pela collection selecionada no cliente.
  const fetchBooks = useCallback(async () => {
    if (!unitAcronym || !selectedCollectionId) {
      setBooks([]);
      setTotalBooks(0);
      return;
    }

    try {
      let allBooks = [];
      let offset = 0;
      const limit = 100;
      let count = 0;
      do {
        const response = await listBooks({ limit, offset, unitAcronym, title: debouncedTitleQuery || undefined });
        const results = response?.results || [];
        allBooks = [...allBooks, ...results];
        count = response?.count || 0;
        offset += limit;
      } while (offset < count);

      const collectionBooks = allBooks.filter(
        (book) => String(book.collection) === String(selectedCollectionId)
      );

      const pageStart = currentPage * itemsPerPage;
      setBooks(collectionBooks.slice(pageStart, pageStart + itemsPerPage));
      setTotalBooks(collectionBooks.length);
    } catch (error) {
      console.error('Erro ao buscar livros:', error);
      setBooks([]);
      setTotalBooks(0);
    }
  }, [unitAcronym, listBooks, selectedCollectionId, currentPage, itemsPerPage, debouncedTitleQuery]);

  const handlePageClick = ({ selected }) => {
    setCurrentPage(selected);
  };

  useEffect(() => {
    fetchBooks();
  }, [fetchBooks]);

  const handleCreateCollection = async (e) => {
    e.preventDefault();
    await runWithSubmitLock(async () => {
      if (effectiveUnitId === null || effectiveUnitId === undefined) return;

      const response = await createCollection({
        ...newCollectionData,
        unit: effectiveUnitId,
      });

      if (response) {
        await fetchCollections();
        setIsCollectionModalOpen(false);
        setNewCollectionData({
          control: true,
          maintenance: 'Demanda',
          unit: effectiveUnitId,
        });
      }
    });
  };

  const handleCreateBook = async (e) => {
    e.preventDefault();
    await runWithSubmitLock(async () => {
      if (!selectedCollectionId || effectiveUnitId === null || effectiveUnitId === undefined) return;

      const response = await createBook({
        ...newBookData,
        collection: selectedCollectionId,
        unit: effectiveUnitId,
      });

      if (response) {
        await fetchBooks();
        setIsBookModalOpen(false);
        resetNewBookData();
      }
    });
  };

  const handleEditBookClick = (book) => {
    setEditBookData(book);
    setIsEditBookModalOpen(true);
  };

  const handleEditBookSave = async (e) => {
    e.preventDefault();
    await runWithSubmitLock(async () => {
      if (!editBookData?.id) return;

      const response = await editBook(editBookData.id, editBookData);
      if (response) {
        await fetchBooks();
        setIsEditBookModalOpen(false);
      }
    });
  };

  const getBookStatusColor = (status) => {
    if (status === 'Bom Estado') return 'green-text';
    if (status === 'Mau Estado') return 'blue-text';
    if (status === 'Baixado') return 'red-text';
    return '';
  };

  const hasEffectiveUnit = effectiveUnitId !== null && effectiveUnitId !== undefined;

  return (
    <div>
      <Sidebar />
      <Header />
      <div className='container social-container admission-page'>
        <div className="admission-header">
          <div className="admission-title">
            <h2>Acervo</h2>
            <p>Cadastro e manutenção de acervos e livros da unidade ativa.</p>
          </div>
        </div>

        {!hasEffectiveUnit ? (
          <div className="health-card unit-details-grid">
            <p>Selecione uma unidade ativa no topo para gerenciar o acervo.</p>
          </div>
        ) : (
          <>
            <div className='admission-actions'>
              {collections.length === 0 && (
                <button className='btnAdcionar' onClick={() => setIsCollectionModalOpen(true)}>
                  <img src={PlusCircle} alt='Ícone' /> Adicionar Acervo
                </button>
              )}
            </div>

            <div className='tab unit-tabs'>
              {collections.map((collection) => (
                <button
                  key={collection.id}
                  className={String(selectedCollectionId) === String(collection.id) ? 'active' : ''}
                  onClick={() => { setSelectedCollectionId(collection.id); setCurrentPage(0); }}
                >
                  {collection.maintenance}
                </button>
              ))}
              {totalBooks > 0 && (
                <span className="seduc-badge" style={{ marginLeft: 'auto', alignSelf: 'center', marginRight: '12px' }}>
                  {totalBooks} livro{totalBooks !== 1 ? 's' : ''} cadastrado{totalBooks !== 1 ? 's' : ''}
                </span>
              )}
            </div>

            {selectedCollectionId ? (
              <div>
                <div className="admission-actions">
                  <button className='btnAdcionar' onClick={() => setIsBookModalOpen(true)}>
                    <img src={PlusCircle} alt='Ícone' /> Adicionar Livro
                  </button>
                  <br></br>
                  <br></br>
                </div>
                <div className="seduc-search-wrapper">
                  <FontAwesomeIcon icon={faSearch} className="seduc-search-icon" />
                  <input
                    type="text"
                    className="seduc-search"
                    placeholder="Buscar livro por título (mínimo 3 letras)"
                    value={titleQuery}
                    onChange={(e) => setTitleQuery(e.target.value.toUpperCase())}
                  />
                </div>
                <br></br>
                <div className="content-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Título</th>
                        <th>Autor</th>
                        <th>Páginas</th>
                        <th>Origem</th>
                        <th>Status</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {books.length > 0 ? (
                        books.map((book) => (
                          <tr key={book.id}>
                            <td>{book.title}</td>
                            <td>{book.author}</td>
                            <td>{book.number_pages}</td>
                            <td>{book.origem}</td>
                            <td>
                              <span className={getBookStatusColor(book.status)}>{book.status}</span>
                            </td>
                            <td>
                              <button onClick={() => handleEditBookClick(book)} className='btnEditar'>
                                <FontAwesomeIcon icon={faPencilAlt} />
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="6">Nenhum livro encontrado.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {totalBooks > itemsPerPage && (
                  <ReactPaginate
                    previousLabel={'<'}
                    nextLabel={'>'}
                    breakLabel={'...'}
                    pageCount={Math.ceil(totalBooks / itemsPerPage)}
                    pageRangeDisplayed={3}
                    marginPagesDisplayed={1}
                    onPageChange={handlePageClick}
                    forcePage={currentPage}
                    containerClassName={'pagination'}
                    activeClassName={'activeNumber'}
                    previousClassName={'prev'}
                    nextClassName={'next'}
                    pageClassName={'page'}
                    breakClassName={'break-me'}
                  />
                )}
              </div>
            ) : (
              <div className="health-card unit-details-grid">
                <p>Nenhum acervo cadastrado para a unidade selecionada.</p>
              </div>
            )}
          </>
        )}
      </div>

      {isCollectionModalOpen && (
        <Modal title={'Adicionar Acervo'} isOpen={isCollectionModalOpen} closeModal={() => setIsCollectionModalOpen(false)}>
          <form onSubmit={handleCreateCollection} className="unit-modal-form unit-modal-grid">
            <div className="unit-modal-field span-2">
              <label>Manutenção</label>
              <select
                value={newCollectionData.maintenance}
                onChange={(e) => setNewCollectionData({ ...newCollectionData, maintenance: e.target.value })}
              >
                <option value="Diaria">Diária</option>
                <option value="Semanal">Semanal</option>
                <option value="Mensal">Mensal</option>
                <option value="Demanda">Demanda</option>
                <option value="Nenhuma">Nenhuma</option>
              </select>
            </div>
            <div className="unit-modal-actions">
              <button type="submit" className="btnConfirmar" disabled={isSubmitting}>
                {isSubmitting ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {isBookModalOpen && (
        <Modal title={'Adicionar Livro'} isOpen={isBookModalOpen} closeModal={() => setIsBookModalOpen(false)}>
          <form onSubmit={handleCreateBook} className="unit-modal-form unit-modal-grid">
            <div className="unit-modal-field">
              <label>Data de Entrada *</label>
              <input
                type="date"
                value={newBookData.entry_date}
                onChange={(e) => setNewBookData({ ...newBookData, entry_date: e.target.value })}
                required
              />
            </div>
            <div className="unit-modal-field span-2">
              <label>Título *</label>
              <input
                type="text"
                value={newBookData.title}
                onChange={(e) => setNewBookData({ ...newBookData, title: e.target.value.toUpperCase() })}
                required
              />
            </div>
            <div className="unit-modal-field span-2">
              <label>Autor *</label>
              <input
                type="text"
                value={newBookData.author}
                onChange={(e) => setNewBookData({ ...newBookData, author: e.target.value })}
                required
              />
            </div>
            <div className="unit-modal-field">
              <label>Edição</label>
              <input
                type="text"
                value={newBookData.edition}
                onChange={(e) => setNewBookData({ ...newBookData, edition: e.target.value })}
              />
            </div>
            <div className="unit-modal-field span-2">
              <label>Editora *</label>
              <input
                type="text"
                value={newBookData.publisher}
                onChange={(e) => setNewBookData({ ...newBookData, publisher: e.target.value })}
                required
              />
            </div>
            <div className="unit-modal-field">
              <label>Data de Publicação *</label>
              <input
                type="date"
                value={newBookData.publication_date}
                onChange={(e) => setNewBookData({ ...newBookData, publication_date: e.target.value })}
                required
              />
            </div>
            <div className="unit-modal-field">
              <label>Número de Páginas *</label>
              <input
                type="number"
                value={newBookData.number_pages}
                onChange={(e) => setNewBookData({ ...newBookData, number_pages: e.target.value })}
                required
              />
            </div>
            <div className="unit-modal-field">
              <label>Gênero</label>
              <input
                type="text"
                value={newBookData.gender}
                onChange={(e) => setNewBookData({ ...newBookData, gender: e.target.value })}
              />
            </div>
            <div className="unit-modal-field">
              <label>ISBN *</label>
              <input
                type="text"
                value={newBookData.isbn}
                onChange={(e) => setNewBookData({ ...newBookData, isbn: e.target.value })}
                required
              />
            </div>
            <div className="unit-modal-field">
              <label>ISBN 2 (Opcional)</label>
              <input
                type="text"
                value={newBookData.isbn_two}
                onChange={(e) => setNewBookData({ ...newBookData, isbn_two: e.target.value })}
              />
            </div>
            <div className="unit-modal-field span-2">
              <label>Tradutor(es) (Opcional)</label>
              <input
                type="text"
                value={newBookData.translators}
                onChange={(e) => setNewBookData({ ...newBookData, translators: e.target.value })}
              />
            </div>
            <div className="unit-modal-field">
              <label>É Original? *</label>
              <select
                value={newBookData.original}
                onChange={(e) => setNewBookData({ ...newBookData, original: e.target.value === 'true' })}
                required
              >
                <option value="true">Sim</option>
                <option value="false">Não</option>
              </select>
            </div>
            <div className="unit-modal-field">
              <label>Status *</label>
              <select
                value={newBookData.status}
                onChange={(e) => setNewBookData({ ...newBookData, status: e.target.value })}
                required
              >
                <option value="">Selecione</option>
                <option value="Bom Estado">Bom Estado</option>
                <option value="Mau Estado">Mau Estado</option>
              </select>
            </div>
            <div className="unit-modal-field">
              <label>Origem *</label>
              <select
                value={newBookData.origem}
                onChange={(e) => setNewBookData({ ...newBookData, origem: e.target.value })}
                required
              >
                <option value="">Selecione</option>
                <option value="Publico">Publico</option>
                <option value="Privado">Privado</option>
                <option value="Misto">Misto</option>
              </select>
            </div>
            <div className="unit-modal-actions">
              <button type="submit" className="btnConfirmar" disabled={isSubmitting}>
                {isSubmitting ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {isEditBookModalOpen && editBookData && (
        <Modal title={'Editar Livro'} isOpen={isEditBookModalOpen} closeModal={() => setIsEditBookModalOpen(false)}>
          <form onSubmit={handleEditBookSave} className="unit-modal-form unit-modal-grid">
            <div className="unit-modal-field">
              <label>Data de Entrada</label>
              <input
                type="date"
                value={editBookData.entry_date}
                onChange={(e) => setEditBookData({ ...editBookData, entry_date: e.target.value })}
              />
            </div>
            <div className="unit-modal-field span-2">
              <label>Título</label>
              <input
                type="text"
                value={editBookData.title}
                onChange={(e) => setEditBookData({ ...editBookData, title: e.target.value.toUpperCase() })}
              />
            </div>
            <div className="unit-modal-field span-2">
              <label>Autor</label>
              <input
                type="text"
                value={editBookData.author}
                onChange={(e) => setEditBookData({ ...editBookData, author: e.target.value })}
              />
            </div>
            <div className="unit-modal-field">
              <label>Edição</label>
              <input
                type="text"
                value={editBookData.edition}
                onChange={(e) => setEditBookData({ ...editBookData, edition: e.target.value })}
              />
            </div>
            <div className="unit-modal-field span-2">
              <label>Editora</label>
              <input
                type="text"
                value={editBookData.publisher}
                onChange={(e) => setEditBookData({ ...editBookData, publisher: e.target.value })}
              />
            </div>
            <div className="unit-modal-field">
              <label>Data de Publicação</label>
              <input
                type="date"
                value={editBookData.publication_date}
                onChange={(e) => setEditBookData({ ...editBookData, publication_date: e.target.value })}
              />
            </div>
            <div className="unit-modal-field">
              <label>Número de Páginas</label>
              <input
                type="number"
                value={editBookData.number_pages}
                onChange={(e) => setEditBookData({ ...editBookData, number_pages: e.target.value })}
              />
            </div>
            <div className="unit-modal-field">
              <label>Gênero</label>
              <input
                type="text"
                value={editBookData.gender || ''}
                onChange={(e) => setEditBookData({ ...editBookData, gender: e.target.value })}
              />
            </div>
            <div className="unit-modal-field">
              <label>ISBN</label>
              <input
                type="text"
                value={editBookData.isbn}
                onChange={(e) => setEditBookData({ ...editBookData, isbn: e.target.value })}
              />
            </div>
            <div className="unit-modal-field">
              <label>ISBN 2 (Opcional)</label>
              <input
                type="text"
                value={editBookData.isbn_two || ''}
                onChange={(e) => setEditBookData({ ...editBookData, isbn_two: e.target.value })}
              />
            </div>
            <div className="unit-modal-field span-2">
              <label>Tradutor(es) (Opcional)</label>
              <input
                type="text"
                value={editBookData.translators || ''}
                onChange={(e) => setEditBookData({ ...editBookData, translators: e.target.value })}
              />
            </div>
            <div className="unit-modal-field">
              <label>É Original?</label>
              <select
                value={editBookData.original}
                onChange={(e) => setEditBookData({ ...editBookData, original: e.target.value === 'true' })}
              >
                <option value="true">Sim</option>
                <option value="false">Não</option>
              </select>
            </div>
            <div className="unit-modal-field">
              <label>Status</label>
              <select
                value={editBookData.status || ''}
                onChange={(e) => setEditBookData({ ...editBookData, status: e.target.value })}
              >
                <option value="">Selecione</option>
                <option value="Bom Estado">Bom Estado</option>
                <option value="Mau Estado">Mau Estado</option>
                <option value="Baixado">Baixado</option>
              </select>
            </div>
            {editBookData.status === 'Baixado' && (
              <div className="unit-modal-field">
                <label>Data de Baixa</label>
                <input
                  type="date"
                  value={editBookData.discharge_date ? editBookData.discharge_date.split('T')[0] : ''}
                  onChange={(e) => setEditBookData({ ...editBookData, discharge_date: e.target.value })}
                />
              </div>
            )}
            <div className="unit-modal-field">
              <label>Origem</label>
              <select
                value={editBookData.origem || ''}
                onChange={(e) => setEditBookData({ ...editBookData, origem: e.target.value })}
              >
                <option value="">Selecione</option>
                <option value="Publico">Publico</option>
                <option value="Privado">Privado</option>
                <option value="Misto">Misto</option>
              </select>
            </div>
            <div className="unit-modal-actions">
              <button type="submit" className="btnConfirmar" disabled={isSubmitting}>
                {isSubmitting ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

export default AcervoEducacao;
